// packages/web-ui/src/state/composer.ts
//
// Story W2-S7 — local zustand store for the `<Composer>` component.
//
// Scope (per the dispatch anti-interference rules): this slice is
// independent of the W2-S5 chat slice. The composer owns its own:
//   - textarea `value` + `caret`
//   - resolved mention `chips` (placeholder tokens written into `value`)
//   - pending file `attachments` (uploadId + contentType, populated by the
//     W2-S6 `<FileUploadConfirm>` card)
//   - `slashMode` (whether the inline `<SlashMenu>` is open and, when in
//     parameter-form mode, which command + params)
//   - `globalPaletteOpen` (whether the centred `<GlobalPalette>` modal is up)
//   - drag-active border state (visual highlight while dragging files over)
//
// The chat panel's `onSend` callback is wired externally (it dispatches into
// the W2-S5 chat slice's `sendMessage(sessionId, body, mentions)`); this slice
// does NOT modify chat state.

import { createStore, type StoreApi } from 'zustand/vanilla';
import type {
  KbEntityKind,
  MentionCandidate,
} from '@tessa-lean/base/api-schemas/index.js';

/** A resolved mention chip living inside the composer textarea. */
export interface ComposerChip {
  /** Stable id (uuid or counter) used as React key + removal anchor. */
  id: string;
  /** Resolved entity id from the KB. `null` for the AC #6 ambiguous sentinel. */
  entityId: string | null;
  /** SysML kind — drives the chip icon and quick-filter behaviour. */
  kind: KbEntityKind;
  /** Human-readable label shown inside the chip. */
  chipText: string;
  /** Full qualified name (hover tooltip + serialised on send). */
  qualifiedName: string;
  /** Char offset in `value` where the chip's placeholder token begins. */
  insertOffset: number;
}

/** Pending file upload attached to the next outgoing chat message. */
export interface ComposerAttachment {
  uploadId: string;
  contentType: string;
  /** Display name for the attachment chip (filename). */
  name: string;
}

/** Inline slash-menu mode descriptor. */
export interface SlashMode {
  /** Command id currently active in parameter-form mode (when `phase === 'params'`). */
  commandId: string | null;
  /** Phase: `'list'` shows the command list; `'params'` shows the parameter form. */
  phase: 'list' | 'params';
  /** Parameter values entered in the form. */
  params: Record<string, unknown>;
}

export interface ComposerSliceState {
  value: string;
  caret: number;
  chips: ComposerChip[];
  attachments: ComposerAttachment[];
  slashOpen: boolean;
  slashMode: SlashMode;
  globalPaletteOpen: boolean;
  dragActive: boolean;
  /** True when an ambiguous mention is in the chip list (blocks send). */
  hasAmbiguousMention: boolean;
}

export interface ComposerSliceActions {
  setValue: (value: string, caret?: number) => void;
  setCaret: (caret: number) => void;
  insertChipForMention: (
    candidate: MentionCandidate,
    triggerOffset: number,
    triggerLength: number,
  ) => void;
  insertAmbiguousChip: (chipText: string, triggerOffset: number, triggerLength: number) => void;
  removeChip: (chipId: string) => void;
  addAttachment: (attachment: ComposerAttachment) => void;
  removeAttachment: (uploadId: string) => void;
  clearAttachments: () => void;
  setSlashOpen: (open: boolean) => void;
  setSlashPhase: (phase: 'list' | 'params', commandId?: string | null) => void;
  setSlashParam: (name: string, value: unknown) => void;
  resetSlashMode: () => void;
  setGlobalPaletteOpen: (open: boolean) => void;
  setDragActive: (active: boolean) => void;
  /** Reset everything to initial — used after successful send. */
  reset: () => void;
}

export type ComposerSlice = ComposerSliceState & ComposerSliceActions;

/** Sentinel entity id for ambiguous-mention chips (AC #6). */
export const AMBIGUOUS_ENTITY_ID = '__ambiguous__';

/** Zero-width brackets surrounding chip placeholders inside the textarea. */
export const CHIP_TOKEN_OPEN = '​@';
export const CHIP_TOKEN_CLOSE = '​';

export const COMPOSER_INITIAL_STATE: ComposerSliceState = {
  value: '',
  caret: 0,
  chips: [],
  attachments: [],
  slashOpen: false,
  slashMode: { commandId: null, phase: 'list', params: {} },
  globalPaletteOpen: false,
  dragActive: false,
  hasAmbiguousMention: false,
};

let _idCounter = 0;
function defaultGenerateId(): string {
  _idCounter += 1;
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `composer-chip-${_idCounter}`;
}

export interface ComposerSliceConfig {
  generateId?: () => string;
}

/**
 * Build the chip placeholder token written into `value`. The zero-width
 * brackets are invisible to the user but recoverable on send.
 */
export function chipPlaceholder(chipText: string): string {
  return `${CHIP_TOKEN_OPEN}${chipText}${CHIP_TOKEN_CLOSE}`;
}

/**
 * Strip placeholder tokens from `value`, replacing each with `@${chipText}`
 * for orchestrator consumption. Used on send.
 */
export function serialiseValueForSend(value: string): string {
  // Tokens look like `​@<chipText>​`. Replace each with `@<chipText>`.
  return value.replace(/​@([^​]+)​/g, (_, chipText: string) => `@${chipText}`);
}

/** Build a deterministic `chips`-derived `mentions[]` for the API request. */
export function buildMentionsFromChips(
  chips: ReadonlyArray<ComposerChip>,
): { chipText: string; entityId: string }[] {
  return chips
    .filter((c): c is ComposerChip & { entityId: string } => c.entityId !== null && c.entityId !== AMBIGUOUS_ENTITY_ID)
    .map((c) => ({ chipText: c.chipText, entityId: c.entityId }));
}

export function createComposerSlice(config: ComposerSliceConfig = {}): StoreApi<ComposerSlice> {
  const genId = config.generateId ?? defaultGenerateId;

  return createStore<ComposerSlice>((set, get) => ({
    ...COMPOSER_INITIAL_STATE,

    setValue: (value, caret) => {
      set((s) => ({
        ...s,
        value,
        caret: caret ?? s.caret,
        // Auto-clear chips whose placeholder no longer appears in the value
        // (covers the case where the user manually deletes text containing
        // a chip placeholder).
        chips: s.chips.filter((chip) => {
          const placeholder = chipPlaceholder(chip.chipText);
          return value.includes(placeholder);
        }),
      }));
      // Recompute ambiguous flag from the surviving chips.
      set((s) => ({
        ...s,
        hasAmbiguousMention: s.chips.some((c) => c.entityId === AMBIGUOUS_ENTITY_ID),
      }));
    },

    setCaret: (caret) => {
      set((s) => ({ ...s, caret }));
    },

    insertChipForMention: (candidate, triggerOffset, triggerLength) => {
      const placeholder = chipPlaceholder(candidate.chipText);
      const before = get().value.slice(0, triggerOffset);
      const after = get().value.slice(triggerOffset + triggerLength);
      const newValue = `${before}${placeholder}${after}`;
      const newCaret = (before + placeholder).length;
      const chip: ComposerChip = {
        id: genId(),
        entityId: candidate.entityId,
        kind: candidate.kind,
        chipText: candidate.chipText,
        qualifiedName: candidate.qualifiedName,
        insertOffset: triggerOffset,
      };
      set((s) => ({
        ...s,
        value: newValue,
        caret: newCaret,
        chips: [...s.chips, chip],
      }));
    },

    insertAmbiguousChip: (chipText, triggerOffset, triggerLength) => {
      const placeholder = chipPlaceholder(chipText);
      const before = get().value.slice(0, triggerOffset);
      const after = get().value.slice(triggerOffset + triggerLength);
      const newValue = `${before}${placeholder}${after}`;
      const newCaret = (before + placeholder).length;
      const chip: ComposerChip = {
        id: genId(),
        entityId: AMBIGUOUS_ENTITY_ID,
        kind: 'part', // arbitrary; chip is rendered with warning style
        chipText,
        qualifiedName: chipText,
        insertOffset: triggerOffset,
      };
      set((s) => ({
        ...s,
        value: newValue,
        caret: newCaret,
        chips: [...s.chips, chip],
        hasAmbiguousMention: true,
      }));
    },

    removeChip: (chipId) => {
      const chip = get().chips.find((c) => c.id === chipId);
      if (chip === undefined) return;
      const placeholder = chipPlaceholder(chip.chipText);
      const idx = get().value.indexOf(placeholder);
      const newValue =
        idx < 0 ? get().value : get().value.slice(0, idx) + get().value.slice(idx + placeholder.length);
      set((s) => ({
        ...s,
        value: newValue,
        chips: s.chips.filter((c) => c.id !== chipId),
      }));
      set((s) => ({
        ...s,
        hasAmbiguousMention: s.chips.some((c) => c.entityId === AMBIGUOUS_ENTITY_ID),
      }));
    },

    addAttachment: (attachment) => {
      set((s) => ({ ...s, attachments: [...s.attachments, attachment] }));
    },

    removeAttachment: (uploadId) => {
      set((s) => ({
        ...s,
        attachments: s.attachments.filter((a) => a.uploadId !== uploadId),
      }));
    },

    clearAttachments: () => {
      set((s) => ({ ...s, attachments: [] }));
    },

    setSlashOpen: (open) => {
      set((s) => ({
        ...s,
        slashOpen: open,
        slashMode: open ? s.slashMode : { commandId: null, phase: 'list', params: {} },
      }));
    },

    setSlashPhase: (phase, commandId) => {
      set((s) => ({
        ...s,
        slashMode: {
          commandId: commandId ?? s.slashMode.commandId,
          phase,
          params: phase === 'list' ? {} : s.slashMode.params,
        },
      }));
    },

    setSlashParam: (name, value) => {
      set((s) => ({
        ...s,
        slashMode: { ...s.slashMode, params: { ...s.slashMode.params, [name]: value } },
      }));
    },

    resetSlashMode: () => {
      set((s) => ({
        ...s,
        slashOpen: false,
        slashMode: { commandId: null, phase: 'list', params: {} },
      }));
    },

    setGlobalPaletteOpen: (open) => {
      set((s) => ({ ...s, globalPaletteOpen: open }));
    },

    setDragActive: (active) => {
      set((s) => ({ ...s, dragActive: active }));
    },

    reset: () => {
      set(() => ({ ...COMPOSER_INITIAL_STATE }));
    },
  }));
}
